import { useCallback, useEffect, useRef, useState } from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

import type { OpenAPI } from "simplydo/interfaces";
import type { UserMe } from "simplydo/interfaces/types/userTypes";
import { DotPulse } from "simplydo/react/components/DotPulse";
import { Button, Divider, Message, Popup } from "simplydo/react/ui";
import { Input } from "simplydo/react/ui";

import actions from "actions";
import api from "api";
import { useAppDispatch, useAppSelector } from "store";
import { useUserReload } from "utils/useUserReload";
import util from "utils/utils";

const VerifyEmail = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [tryAgain, setTryAgain] = useState(10);
  const [editingEmail, setEditingEmail] = useState<string>("");
  const [editingEmailOpen, setEditingEmailOpen] = useState<boolean>(false);
  const hasAutoResentVerification = useRef(false);
  const user = useAppSelector((state) => state.user);
  const dispatch = useAppDispatch();
  const reloadUser = useUserReload();

  const { t } = useTranslation();

  useEffect(() => {
    const tryAgainTimer = setInterval(
      () => setTryAgain((prev) => Math.max(prev - 1, 0)),
      1000,
    );
    return () => {
      clearInterval(tryAgainTimer);
    };
  }, []);

  // Ping at regular intervals to user endpoint to see if they have verified their email
  useEffect(() => {
    const userMeInterval = setInterval(() => {
      if (document.visibilityState === "visible") {
        api.users.me(
          "",
          (userResponse: OpenAPI.GET<"/users/me">["response"]) => {
            const me = userResponse as UserMe;
            if (util.hasVerifiedEmail(me, me.ownerOrganisation)) {
              clearInterval(userMeInterval);
              dispatch(actions.user.receiveUser(me));
            }
          },
          () => {},
        );
      }
    }, 2000);
    return () => {
      clearInterval(userMeInterval);
    };
  }, [dispatch]);

  const resendVerificationEmail = useCallback(() => {
    setLoading(true);
    const loadingId = toast.loading(
      t("entry.verifyEmail.resend.statusMessageLoading"),
    );
    api.auth.requestEmailVerification(
      "",
      () => {
        setLoading(false);
        toast.success(t("entry.verifyEmail.resend.statusMessageSuccess"), {
          id: loadingId,
        });
      },
      (err) => {
        setLoading(false);
        toast.error(err.message, { id: loadingId });
      },
    );
  }, [t]);

  useEffect(() => {
    if (!user || user?.emails?.[0]?.verified) {
      return;
    }
    if (hasAutoResentVerification.current) {
      return;
    }
    const now = Date.now();
    // If the email was sent more than 2 days ago, we can resend it
    const verifyEmailSentAtMs = user.verifyEmailSentAt
      ? new Date(user.verifyEmailSentAt).getTime()
      : 0;
    const serverResendDue =
      !verifyEmailSentAtMs || now - verifyEmailSentAtMs > 172800000; // 2 days

    if (!serverResendDue) {
      return;
    }

    resendVerificationEmail();
    hasAutoResentVerification.current = true;
    setTryAgain(30);
  }, [resendVerificationEmail, user]);

  const updateEmailAddress = useCallback(() => {
    api.auth.updateEmailAddress(
      { email: editingEmail },
      () => {
        setLoading(false);
        setTryAgain(30);
        setEditingEmail("");
        setEditingEmailOpen(false);
        reloadUser();
        resendVerificationEmail();
      },
      (err) => {
        setLoading(false);
        toast.error(err.message);
      },
    );
  }, [editingEmail, resendVerificationEmail, reloadUser]);

  return (
    <>
      <h3>{t("entry.verifyEmail.title")}</h3>
      <p>{t("entry.verifyEmail.info")}</p>
      <div style={{ display: "flex", gap: 8 }}>
        <Input
          size="small"
          label={t("entry.verifyEmail.yourEmailLabel")}
          readOnly
          value={user?.emails[0]?.address}
        />
        <Popup
          on="click"
          open={editingEmailOpen}
          onClose={() => setEditingEmailOpen(false)}
          trigger={
            <Button
              size="small"
              basic
              content={t("entry.verifyEmail.changeEmailButton")}
              onClick={() => {
                setEditingEmailOpen(true);
                setEditingEmail(user?.emails[0]?.address || "");
              }}
              loading={loading}
            />
          }
          content={
            <div style={{ display: "flex", flexDirection: "column" }}>
              <b>{t("entry.verifyEmail.updateEmail.title")}</b>
              <p>{t("entry.verifyEmail.updateEmail.info")}</p>
              <Input
                value={editingEmail}
                style={{ marginRight: 5 }}
                onChange={(_e, { value }) => {
                  setEditingEmail(value);
                }}
                action={
                  <Button
                    size="small"
                    content={t("generic.save")}
                    onClick={() => {
                      if (editingEmail === user?.emails[0]?.address) {
                        setEditingEmailOpen(false);
                        return;
                      }
                      updateEmailAddress();
                    }}
                  />
                }
              />
            </div>
          }
        />
      </div>
      <Divider section hidden />
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          gap: 16,
        }}
      >
        <p>
          <strong>{t("entry.verifyEmail.waitingText")}</strong>
        </p>
        <DotPulse size={15} />
      </div>
      <Divider section hidden />

      <div>
        <Divider />
        <h5>{t("entry.verifyEmail.resend.title")}</h5>
        <p>{t("entry.verifyEmail.resend.info")}</p>
        <Message size="small">
          {t("entry.verifyEmail.resend.spamReminder")}
        </Message>
        <div style={{ display: "flex" }}>
          <Button
            size="small"
            content={`${t("entry.verifyEmail.resend.action")}${tryAgain > 0 ? ` (${tryAgain})` : ""}`}
            onClick={() => {
              resendVerificationEmail();
              setTryAgain(30);
            }}
            loading={loading}
            disabled={tryAgain > 0}
          />
        </div>
      </div>
    </>
  );
};

export default VerifyEmail;
